import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for debouncing values
 * @param {any} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} Debounced value
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for debouncing API calls
 * @param {Function} apiCall - API function to call
 * @param {any} dependencies - Dependencies to watch
 * @param {number} delay - Delay in milliseconds
 * @returns {Object} { data, loading, error, refetch }
 */
export const useDebouncedApiCall = (apiCall, dependencies = [], delay = 500) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedDependencies = useDebounce(dependencies, delay);

  useEffect(() => {
    let isCancelled = false;

    const executeApiCall = async () => {
      if (!apiCall || typeof apiCall !== 'function') return;

      try {
        setLoading(true);
        setError(null);
        
        const result = await apiCall();
        
        if (!isCancelled) {
          setData(result);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    executeApiCall();

    return () => {
      isCancelled = true;
    };
  }, [debouncedDependencies, apiCall]);

  const refetch = async () => {
    if (!apiCall || typeof apiCall !== 'function') return;

    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refetch };
};

/**
 * Custom hook for debouncing function calls
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @param {Array} deps - Dependencies array for the callback
 * @returns {Function} Debounced function
 */
export const useDebouncedCallback = (callback, delay, deps = []) => {
  const [timeoutId, setTimeoutId] = useState(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = setTimeout(() => {
      callback(...args);
    }, delay);

    setTimeoutId(newTimeoutId);
  }, [callback, delay, timeoutId]);

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
};

export default useDebounce;


